import React, { useEffect, useState } from "react";

import { MenuItemBadgeDto } from "services/menuItemBadges/restModels";
import { MenuItemBadgeRequestDto } from "services/menuItemBadges/restRequests";
import {
    Button,
    ColorPickerDialog,
    ColorSwatch,
    LabeledField,
    LanguageSegmentedControl,
    Modal,
    RadioGroup,
    Stack,
    TextInput,
    ToggleSwitch,
    ChipToggle,
} from "shared/ui";
import { GRADIENT_ANGLE_OPTIONS } from "shared/lib/badgeFill";
import { localizedText } from "shared/lib/localizedMap";
import { useActiveLanguage } from "shared/hooks/useActiveLanguage";

import { BadgePill } from "features/MenuItemBadges/components/BadgePill";
import {
    BADGE_FILL_KIND_OPTIONS,
    BADGE_PLACEMENT_OPTIONS,
} from "features/MenuItemBadges/constants";
import { useMenuItemBadgeForm } from "features/MenuItemBadges/hooks/useMenuItemBadgeForm";

import { Angles, LangRow, Preview, Swatches } from "./styles";

// Какой из трёх цветов сейчас правят — один пикер на всю форму.
type EditedColor = "from" | "to" | "text";

interface BadgeDialogProps {
    open: boolean;
    badge: MenuItemBadgeDto | null;
    locale: string;
    availableLanguages: string[];
    saving: boolean;
    onClose: () => void;
    onSubmit: (values: MenuItemBadgeRequestDto) => Promise<boolean>;
}

export const BadgeDialog = ({
    open,
    badge,
    locale,
    availableLanguages,
    saving,
    onClose,
    onSubmit,
}: BadgeDialogProps) => {
    const form = useMenuItemBadgeForm(badge, open);
    const { activeLang, setActiveLang } = useActiveLanguage(locale, availableLanguages);
    const [editedColor, setEditedColor] = useState<EditedColor | null>(null);

    // Диалог бейджа смонтирован всегда: без сброса форма, закрытая с открытым пикером,
    // в следующий раз открылась бы сразу с пикером поверх.
    useEffect(() => {
        if (!open) setEditedColor(null);
    }, [open]);

    const isGradient = form.fill.kind === "Gradient";

    const colorValue = (color: EditedColor): string => {
        if (color === "from") return form.fill.colorFrom;
        if (color === "to") return form.fill.colorTo;
        return form.textColor;
    };

    const applyColor = (color: string) => {
        if (editedColor === "from") form.setColorFrom(color);
        if (editedColor === "to") form.setColorTo(color);
        if (editedColor === "text") form.setTextColor(color);

        setEditedColor(null);
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={badge ? "Бейдж позиции" : "Новый бейдж"}
            actions={
                <>
                    <Button variant="outlined" color="neutral" onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        variant="contained"
                        disabled={saving || !form.isValid}
                        onClick={() => onSubmit(form.buildRequest())}
                    >
                        Сохранить
                    </Button>
                </>
            }
        >
            <Stack>
                <LangRow>
                    <LanguageSegmentedControl
                        languages={availableLanguages}
                        activeLang={activeLang}
                        primaryLang={locale}
                        onChange={setActiveLang}
                        isMissing={(lang) => !form.title[lang]}
                    />
                </LangRow>

                <LabeledField
                    label="Подпись"
                    hint="Видна гостю. Эмодзи работают: «🌶️ Острый», «🔥 Хит»"
                >
                    <TextInput
                        value={form.title[activeLang] || ""}
                        onChange={(value) => form.setTitle(value, activeLang)}
                        placeholder="Острый"
                    />
                </LabeledField>

                <Preview>
                    <BadgePill
                        title={localizedText(form.title, activeLang) || "Подпись"}
                        fill={form.fill}
                        textColor={form.textColor}
                    />
                </Preview>

                <LabeledField label="Заливка">
                    <RadioGroup
                        value={form.fill.kind}
                        options={BADGE_FILL_KIND_OPTIONS}
                        onChange={form.setFillKind}
                    />
                </LabeledField>

                <Swatches>
                    <ColorSwatch
                        color={form.fill.colorFrom}
                        label={isGradient ? "Цвет слева" : "Цвет фона"}
                        onClick={() => setEditedColor("from")}
                    />
                    {isGradient && (
                        <ColorSwatch
                            color={form.fill.colorTo}
                            label="Цвет справа"
                            onClick={() => setEditedColor("to")}
                        />
                    )}
                    <ColorSwatch
                        color={form.textColor}
                        label="Цвет текста"
                        onClick={() => setEditedColor("text")}
                    />
                </Swatches>

                {isGradient && (
                    <LabeledField label="Направление">
                        <Angles>
                            {GRADIENT_ANGLE_OPTIONS.map((option) => (
                                <ChipToggle
                                    key={option.angleDeg}
                                    active={form.fill.angleDeg === option.angleDeg}
                                    onClick={() => form.setAngleDeg(option.angleDeg)}
                                >
                                    {option.label}
                                </ChipToggle>
                            ))}
                        </Angles>
                    </LabeledField>
                )}

                <LabeledField label="Где показывать">
                    <RadioGroup
                        value={form.placement}
                        options={BADGE_PLACEMENT_OPTIONS}
                        onChange={form.setPlacement}
                    />
                </LabeledField>

                <ToggleSwitch
                    checked={form.isEnabled}
                    onChange={form.setIsEnabled}
                    label="Показывать в меню"
                    hint="Выключенный бейдж исчезает из меню, но остаётся у отмеченных позиций"
                />
            </Stack>

            <ColorPickerDialog
                open={!!editedColor}
                initialColor={editedColor ? colorValue(editedColor) : null}
                title="Выбор цвета"
                onClose={() => setEditedColor(null)}
                onApply={applyColor}
            />
        </Modal>
    );
};
